import type { JSONSerializable } from '@highimpact-frontend/api-types';

// -- type definitions --

/** Parameters for a browser fetch that requests CSV from the measures API. */
export interface MeasureCsvFetchParams {
  url: string;
  body?: JSONSerializable;
  token?: string | null;
}

/** Minimal HTTP client shape used by the Redux measure transport. */
export interface MeasureCsvAxiosClient {
  request(config: {
    url: string;
    method: 'get' | 'post';
    data?: object;
    params?: object;
    headers?: Record<string, string>;
    responseType?: 'text';
  }): Promise<{ data: string }>;
}

// -- helper functions --

/**
 * Request measure data as CSV through the browser fetch API.
 *
 * Uses `Accept: text/csv` and returns the response body as plain text.
 */
export async function fetchMeasureCsvText(
  params: MeasureCsvFetchParams
): Promise<string> {
  const response = await fetch(params.url, {
    method: params.body ? 'POST' : 'GET',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/csv',
      ...(params.token ? { Authorization: `Bearer ${params.token}` } : {}),
    },
    body: params.body ? JSON.stringify(params.body) : undefined,
  });

  if (!response.ok) {
    throw new Error(`CSV export failed with status ${response.status}`);
  }

  return response.text();
}

/**
 * Request measure data as CSV through an axios-like client.
 *
 * Used by the main app's Redux transport which already owns the API client.
 */
export async function fetchMeasureCsvTextWithAxiosClient(
  api: MeasureCsvAxiosClient,
  route: string,
  params: object,
  method: 'get' | 'post'
): Promise<string> {
  const response = await api.request({
    url: route,
    method,
    data: method === 'post' ? params : undefined,
    params: method === 'get' ? params : undefined,
    headers: { Accept: 'text/csv' },
    responseType: 'text',
  });

  return response.data;
}

/** Build a safe local filename for a CSV download. */
export function buildCsvDownloadFilename(baseName: string): string {
  const sanitized = baseName
    .trim()
    .replace(/[^\w\- ]+/g, '')
    .replace(/\s+/g, '_')
    .replace(/^_+|_+$/g, '');

  const stem = sanitized.length > 0 ? sanitized : 'export';

  return stem.toLowerCase().endsWith('.csv') ? stem : `${stem}.csv`;
}

/** Trigger a CSV file download in the user's browser. */
export function downloadCsvText(csvText: string, filename: string): void {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.style.display = 'none';

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}
